import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import { caseStudies } from '../../data/case-studies';

export default function CaseStudies() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A]">
      <SEO titleKey="seo.title.caseStudies" descriptionKey="seo.description.caseStudies" />
      <Header />

      <div className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#1E3A8A]/10 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#10B981]/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Hero Section */}
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 mb-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full mb-6 shadow-sm">
            <i className="ri-trophy-line text-white"></i>
            <span className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              {t('caseStudies.badge')}
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            {t('caseStudies.title')}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('caseStudies.subtitle')}
          </p>
        </div>

        {/* Stats Overview */}
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 mb-24">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-slate-200 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-transparent to-emerald-50 opacity-50"></div>
            <div className="grid md:grid-cols-4 gap-8 relative z-10">
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors duration-300" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {t('caseStudies.stats.customers.value')}
                </div>
                <div className="text-slate-500 font-medium uppercase tracking-wider text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('caseStudies.stats.customers.label')}
                </div>
              </div>
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors duration-300" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {t('caseStudies.stats.increase.value')}
                </div>
                <div className="text-slate-500 font-medium uppercase tracking-wider text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('caseStudies.stats.increase.label')}
                </div>
              </div>
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors duration-300" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {t('caseStudies.stats.rating.value')}
                </div>
                <div className="text-slate-500 font-medium uppercase tracking-wider text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('caseStudies.stats.rating.label')}
                </div>
              </div>
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A] transition-colors duration-300" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {t('caseStudies.stats.recommendation.value')}
                </div>
                <div className="text-slate-500 font-medium uppercase tracking-wider text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('caseStudies.stats.recommendation.label')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Case Studies */}
        <div className="max-w-[1280px] mx-auto px-6 relative z-10">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <div
                key={study.id}
                className="bg-white rounded-3xl overflow-hidden hover:border-slate-300 border border-slate-200 transition-all duration-500 hover:shadow-lg group relative"
              >
                {/* Link wrapper for the whole card to be clickable? Or just a button? The design might prefer a button. 
                      Let's make the card clickable but with a distinct button inside. */}

                <div className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 0 ? '' : 'lg:grid-flow-dense'}`}>
                  <div className={`relative h-[400px] lg:h-auto overflow-hidden ${index % 2 === 0 ? '' : 'lg:col-start-2'}`}>
                    <img
                      src={study.image}
                      alt={t(study.companyKey)}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60"></div>
                  </div>

                  <div className={`p-8 lg:p-12 flex flex-col justify-center relative ${index % 2 === 0 ? '' : 'lg:col-start-1 lg:row-start-1'}`}>
                    {/* Decorative background for text area */}
                    <div className="absolute inset-0 bg-slate-50/50 backdrop-blur-sm -z-10"></div>

                    <div className="inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-200 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6 w-fit backdrop-blur-md" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                      {t(study.industryKey)}
                    </div>

                    <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 transition-colors duration-300 group-hover:bg-clip-text group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-[#10B981] group-hover:to-[#1E3A8A]" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                      {t(study.companyKey)}
                    </h2>

                    <div className="space-y-6 mb-8">
                      <div className="relative pl-6 border-l-2 border-[#10B981]/50">
                        <h3 className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] uppercase tracking-wider mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                          {t('caseStudies.challenge')}
                        </h3>
                        <p className="text-slate-600 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                          {t(study.challengeKey)}
                        </p>
                      </div>
                      <div className="relative pl-6 border-l-2 border-[#1E3A8A]/50">
                        <h3 className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] uppercase tracking-wider mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                          {t('caseStudies.solution')}
                        </h3>
                        <p className="text-slate-600 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                          {t(study.solutionKey)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-8">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-colors duration-300 shadow-sm">
                          <div className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                            {t(result.metricKey)}
                          </div>
                          <div className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wide" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                            {t(result.labelKey)}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-slate-200 relative group/quote mb-6">
                      <div className="absolute -top-3 -left-3 bg-white p-2 rounded-full border border-slate-200 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]">
                        <i className="ri-double-quotes-l text-xl"></i>
                      </div>
                      <p className="text-slate-600 italic leading-relaxed mb-4 pl-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                        "{t(study.testimonialKey)}"
                      </p>
                      <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] font-orbitron text-right border-t border-slate-100 pt-3">
                        {t('caseStudies.duration')} <span className="text-[#0F172A]">{t(study.durationKey)}</span>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <div className="mt-auto pt-4">
                      <Link to={`/case-studies/${study.slug}`} className="inline-flex items-center gap-2 text-[#0F172A] font-bold uppercase tracking-wider hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors group-hover:translate-x-2 transform duration-300">
                        Details ansehen <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-[1280px] mx-auto px-6 mt-24">
          <div className="bg-white rounded-3xl p-12 text-center relative overflow-hidden shadow-lg border border-slate-200 group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-transparent to-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="w-20 h-20 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-2xl flex items-center justify-center mx-auto mb-8 border border-blue-100 animate-pulse">
                <i className="ri-rocket-line text-white text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                {t('caseStudies.cta.title')}
              </h2>
              <p className="text-slate-600 text-lg mb-10" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {t('caseStudies.cta.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link
                  to="/#preise"
                  className="px-8 py-4 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white rounded-lg hover:shadow-[0_0_15px_rgba(16,185,129,0.4)] transition-all duration-300 font-bold uppercase tracking-wider shadow-md inline-flex items-center justify-center gap-2"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  {t('caseStudies.cta.primary')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="mailto:info@reputrust.ai?subject=Demo-Anfrage"
                  className="px-8 py-4 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all duration-300 font-medium uppercase tracking-wider inline-flex items-center justify-center gap-2"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  {t('caseStudies.cta.secondary')}
                  <i className="ri-calendar-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
